/**
 * Sub-Tahap 5C — Gelombang 3 (Finalisasi UI)
 * Halaman baru: Coretax Import/Export, Executive Dashboard Pajak, Faktur Pengganti Drill-down.
 *
 * Semua UI Bahasa Indonesia. Tidak menampilkan data dummy — jika kosong tampilkan
 * "TIDAK ADA DATA". Coretax jelas dilabeli "SIMULASI"/"BELUM TERHUBUNG" sesuai instruksi.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { formatRupiah, formatDateID } from "@/lib/format";
import {
  Upload, Download, Link2, RefreshCw, FileSpreadsheet, CheckCircle2, XCircle,
  AlertTriangle, ArrowRight, Filter, BarChart3, ClipboardCheck, ShieldCheck,
  FileText, Search, GitBranch,
} from "lucide-react";

const DATA_TYPES = [
  { code: "faktur_keluaran", label: "Faktur Keluaran" },
  { code: "faktur_masukan", label: "Faktur Masukan" },
  { code: "bukti_potong", label: "Bukti Potong" },
  { code: "tax_payments", label: "Pembayaran Pajak" },
];

const TAX_TYPES = ["PPN", "PPh21", "PPh22", "PPh23", "PPh25", "PPhFinal"];

function Crumb({ path }) {
  return <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">{path.map((p, i) => (<span key={i}>{p}{i < path.length - 1 && " › "}</span>))}</div>;
}

function Stat({ label, value, tone = "slate", sub, testid }) {
  const map = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    sky: "bg-sky-50 border-sky-200 text-sky-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    blue: "bg-blue-50 border-blue-200 text-blue-900",
  };
  return (
    <div className={`border rounded-md p-4 ${map[tone]}`} data-testid={testid}>
      <div className="text-xs opacity-80">{label}</div>
      <div className="font-mono-num text-lg font-semibold mt-1">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

/* ============================================================
 *  1) CORETAX IMPORT / EXPORT CENTER
 * ============================================================ */
function parseCSV(text) {
  // Simple CSV parser: split lines, strip, split by comma. Values in quotes are handled minimally.
  const lines = text.replace(/\r/g, "").split("\n").filter(l => l.trim().length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };
  const splitLine = (l) => {
    // support double-quoted fields
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < l.length; i++) {
      const ch = l[i];
      if (ch === '"') { inQ = !inQ; continue; }
      if (ch === ',' && !inQ) { out.push(cur); cur = ""; continue; }
      cur += ch;
    }
    out.push(cur);
    return out.map(x => x.trim());
  };
  const headers = splitLine(lines[0]);
  const rows = lines.slice(1).map(l => {
    const vals = splitLine(l);
    const obj = {};
    headers.forEach((h, i) => obj[h] = vals[i] ?? "");
    return obj;
  });
  return { headers, rows };
}

export function CoretaxIOPage() {
  const [dataType, setDataType] = useState("faktur_keluaran");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState("");
  const [expHistory, setExpHistory] = useState([]);
  const [impHistory, setImpHistory] = useState([]);
  const [csvText, setCsvText] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("export");
  const [suggestBatchId, setSuggestBatchId] = useState("");
  const [suggestions, setSuggestions] = useState(null);

  const loadHistory = async () => {
    try {
      const [eh, ih] = await Promise.all([
        api.get("/coretax5c/io/export/history?limit=50"),
        api.get("/coretax5c/io/import/history?limit=50"),
      ]);
      setExpHistory(eh.data || []); setImpHistory(ih.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadHistory(); }, []);

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/coretax5c/io/template/${dataType}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `template_${dataType}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Template CSV diunduh");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doExport = async () => {
    try {
      const params = {};
      if (year) params.year = year;
      if (month) params.month = month;
      const res = await api.get(`/coretax5c/io/export/${dataType}`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `export_${dataType}_${year}${month ? `-${String(month).padStart(2,"0")}` : ""}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV berhasil");
      await loadHistory();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result || "");
    reader.readAsText(f);
  };

  const doPreview = async () => {
    if (!csvText.trim()) { toast.error("Upload atau tempel CSV dulu"); return; }
    const { rows } = parseCSV(csvText);
    if (rows.length === 0) { toast.error("CSV tidak memiliki baris data"); return; }
    setBusy(true); setPreviewResult(null);
    try {
      const { data } = await api.post("/coretax5c/io/import/preview", { data_type: dataType, rows });
      setPreviewResult(data);
      toast.success(`Preview: ${data.summary.valid} valid, ${data.summary.warning} warning, ${data.summary.error} error`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doCommit = async () => {
    if (!previewResult?.batch_id) return;
    if (!window.confirm(`Commit ${previewResult.summary.valid} baris valid? Baris error akan dilewati.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/coretax5c/io/import/commit", { batch_id: previewResult.batch_id });
      toast.success(`Commit selesai: ${data.committed_count} baris diproses, ${data.skipped_count} dilewati.`);
      setPreviewResult(null); setCsvText("");
      await loadHistory();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const loadSuggestions = async (batchId) => {
    setSuggestBatchId(batchId); setSuggestions(null);
    try {
      const { data } = await api.get(`/coretax5c/io/import/${batchId}/match-suggestions`);
      setSuggestions(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmMatch = async (faktur_id, candidate, fk_type) => {
    try {
      const body = { faktur_id };
      if (fk_type === "KELUARAN") body.invoice_id = candidate.source_id; else body.bill_id = candidate.source_id;
      await api.post("/coretax5c/io/import/confirm-match", body);
      toast.success(`Match dikonfirmasi: ${candidate.source_number}`);
      await loadSuggestions(suggestBatchId);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rejectMatch = async (faktur_id) => {
    if (!window.confirm("Batalkan match yang sudah dikonfirmasi?")) return;
    try {
      await api.post("/coretax5c/io/import/reject-match", { faktur_id });
      toast.success("Match dibatalkan");
      await loadSuggestions(suggestBatchId);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 page-fade" data-testid="coretax-io-page">
      <div>
        <Crumb path={["Perpajakan", "Coretax Import/Export"]} />
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Coretax Import / Export</h1>
            <p className="text-slate-500 mt-1 text-sm">Pertukaran data batch dengan sistem Coretax via CSV. Konfigurasi mapping tersedia di menu Profil & Aturan Pajak.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadHistory} data-testid="io-refresh"><RefreshCw className="h-4 w-4 mr-2" />Muat Ulang Riwayat</Button>
          </div>
        </div>
      </div>

      <Card><CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div><Label>Jenis Data</Label>
          <Select value={dataType} onValueChange={setDataType}>
            <SelectTrigger data-testid="io-data-type"><SelectValue /></SelectTrigger>
            <SelectContent>{DATA_TYPES.map(t => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Tahun</Label><Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || year)} data-testid="io-year" /></div>
        <div><Label>Bulan</Label>
          <Select value={month === "" ? "ALL" : String(month)} onValueChange={(v) => setMonth(v === "ALL" ? "" : Number(v))}>
            <SelectTrigger data-testid="io-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua</SelectItem>
              {Array.from({length:12}).map((_,i) => <SelectItem key={i+1} value={String(i+1)}>{String(i+1).padStart(2,"0")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <Button variant="outline" onClick={downloadTemplate} data-testid="io-template-btn"><Download className="h-4 w-4 mr-2" />Template CSV</Button>
        </div>
      </CardContent></Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="export" data-testid="tab-export"><Download className="h-4 w-4 mr-1" />Export</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import"><Upload className="h-4 w-4 mr-1" />Import</TabsTrigger>
          <TabsTrigger value="match" data-testid="tab-match"><Link2 className="h-4 w-4 mr-1" />Konfirmasi Match</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><ClipboardCheck className="h-4 w-4 mr-1" />Riwayat Batch</TabsTrigger>
        </TabsList>

        <TabsContent value="export">
          <Card><CardContent className="p-4 space-y-3">
            <p className="text-sm text-slate-500">Export data internal ke CSV format Coretax. File akan diunduh langsung dan tercatat di riwayat.</p>
            <Button onClick={doExport} data-testid="io-export-btn"><FileSpreadsheet className="h-4 w-4 mr-2" />Export CSV {DATA_TYPES.find(t => t.code === dataType)?.label}</Button>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="import">
          <Card><CardContent className="p-4 space-y-3">
            <div className="border-l-4 border-sky-500 bg-sky-50 p-3 rounded text-sm">
              <b>Alur:</b> 1) Unggah CSV (sesuai template) · 2) Preview validasi · 3) Commit atomic. Baris ERROR/masa terkunci otomatis dilewati.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Pilih File CSV</Label>
                <Input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="io-file-input" />
              </div>
              <div>
                <Label>Atau tempel isi CSV di sini</Label>
                <textarea className="w-full border rounded p-2 text-xs font-mono h-24" value={csvText} onChange={(e) => setCsvText(e.target.value)} placeholder="header1,header2,..." data-testid="io-csv-textarea" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={doPreview} disabled={busy || !csvText.trim()} data-testid="io-preview-btn"><Search className="h-4 w-4 mr-2" />Preview Validasi</Button>
              {previewResult && <Button onClick={doCommit} disabled={busy || previewResult.summary.valid === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="io-commit-btn"><CheckCircle2 className="h-4 w-4 mr-2" />Commit ({previewResult.summary.valid} valid)</Button>}
            </div>
            {previewResult && (
              <div className="border rounded p-3 bg-slate-50 space-y-2" data-testid="io-preview-result">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
                  <div className="bg-white p-2 rounded border"><b>Batch ID:</b><br /><span className="font-mono">{previewResult.batch_id.slice(0,8)}…</span></div>
                  <div className="bg-white p-2 rounded border"><b>Total:</b> {previewResult.summary.total}</div>
                  <div className="bg-emerald-50 p-2 rounded border border-emerald-200"><b>Valid:</b> {previewResult.summary.valid}</div>
                  <div className="bg-amber-50 p-2 rounded border border-amber-200"><b>Warning:</b> {previewResult.summary.warning}</div>
                  <div className="bg-rose-50 p-2 rounded border border-rose-200"><b>Error:</b> {previewResult.summary.error}</div>
                  <div className="bg-white p-2 rounded border"><b>Duplikat:</b> {previewResult.summary.duplicates} · <b>Locked:</b> {previewResult.summary.locked_period}</div>
                </div>
                <div className="max-h-80 overflow-auto border rounded bg-white">
                  <Table>
                    <TableHeader><TableRow><TableHead className="w-8">#</TableHead><TableHead>Status</TableHead><TableHead>Data</TableHead><TableHead>Issue</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {previewResult.rows.map((r, i) => (
                        <TableRow key={i} data-testid={`io-preview-row-${i}`}>
                          <TableCell className="text-xs">{r.row_index + 1}</TableCell>
                          <TableCell>
                            {r.errors.length > 0 ? <Badge className="bg-rose-600">ERROR</Badge>
                             : r.warnings.length > 0 ? <Badge className="bg-amber-500">WARNING</Badge>
                             : <Badge className="bg-emerald-600">VALID</Badge>}
                          </TableCell>
                          <TableCell className="text-xs font-mono truncate max-w-[300px]">
                            {Object.entries(r.cleaned || {}).filter(([k,v]) => v !== null && v !== "").slice(0,3).map(([k,v]) => `${k}=${v}`).join(", ")}
                          </TableCell>
                          <TableCell className="text-xs">
                            {r.errors.map((e, ei) => <div key={`e-${ei}`} className="text-rose-700">✗ {e.msg}</div>)}
                            {r.warnings.map((w, wi) => <div key={`w-${wi}`} className="text-amber-700">! {w.msg}</div>)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="text-xs text-slate-500">{previewResult.note}</div>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="match">
          <Card><CardContent className="p-4 space-y-3">
            <p className="text-sm text-slate-500">Cocokkan faktur hasil import Coretax dengan Invoice/Bill sumber. Konfirmasi match adalah metadata linking — tidak mempengaruhi GL.</p>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label>Pilih Batch Import (khusus faktur)</Label>
                <Select value={suggestBatchId || "NONE"} onValueChange={(v) => v !== "NONE" && loadSuggestions(v)}>
                  <SelectTrigger data-testid="io-match-batch"><SelectValue placeholder="Pilih batch..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">— Pilih —</SelectItem>
                    {impHistory.filter(b => ["faktur_keluaran", "faktur_masukan"].includes(b.data_type) && b.status === "COMMITTED").map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.data_type} · {b.id.slice(0,8)}… · {formatDateID(b.created_at)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {suggestBatchId && <Button variant="outline" onClick={() => loadSuggestions(suggestBatchId)} data-testid="io-match-refresh"><RefreshCw className="h-4 w-4 mr-2" />Muat Ulang</Button>}
            </div>
            {suggestions && (
              <div className="space-y-3">
                {suggestions.items.length === 0 && <div className="text-slate-400 text-sm py-6 text-center">TIDAK ADA FAKTUR IMPORT DALAM BATCH INI</div>}
                {suggestions.items.map((it, i) => (
                  <Card key={it.faktur_id} className="border-slate-200"><CardContent className="p-3 space-y-2" data-testid={`match-item-${i}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="text-sm">
                        <div><b className="font-mono">{it.faktur_number}</b> · {formatDateID(it.faktur_date)}</div>
                        <div className="text-slate-500 text-xs">{it.counterparty_name} · {it.counterparty_npwp}</div>
                        <div className="text-slate-600 text-xs">Ref: {it.reference_number || "-"} · DPP {formatRupiah(it.dpp)} · PPN {formatRupiah(it.ppn)}</div>
                      </div>
                      <div>
                        {it.match_status === "SUDAH_DIKONFIRMASI" ? (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-emerald-600">SUDAH DICOCOKKAN → {it.matched_source_number}</Badge>
                            <Button size="sm" variant="ghost" onClick={() => rejectMatch(it.faktur_id)} data-testid={`match-reject-${i}`}><XCircle className="h-3 w-3" /></Button>
                          </div>
                        ) : (
                          <Badge className="bg-amber-500">BELUM DICOCOKKAN</Badge>
                        )}
                      </div>
                    </div>
                    {it.match_status !== "SUDAH_DIKONFIRMASI" && (
                      <div>
                        {it.candidates.length === 0 ? (
                          <div className="text-xs text-slate-400 italic">Tidak ada kandidat otomatis — cocokkan manual dari daftar faktur.</div>
                        ) : (
                          <div className="border rounded overflow-hidden">
                            <Table>
                              <TableHeader><TableRow><TableHead>Skor</TableHead><TableHead>Nomor Sumber</TableHead><TableHead>Tanggal</TableHead><TableHead>Nilai</TableHead><TableHead>Alasan</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
                              <TableBody>
                                {it.candidates.map((c, ci) => (
                                  <TableRow key={c.source_id} data-testid={`match-candidate-${i}-${ci}`}>
                                    <TableCell><Badge className={c.score >= 80 ? "bg-emerald-600" : "bg-sky-500"}>{c.score}</Badge></TableCell>
                                    <TableCell className="font-mono text-xs">{c.source_number}</TableCell>
                                    <TableCell className="text-xs">{formatDateID(c.date)}</TableCell>
                                    <TableCell className="font-mono-num text-xs">{formatRupiah(c.gross_total)}</TableCell>
                                    <TableCell className="text-xs">{c.reason}</TableCell>
                                    <TableCell><Button size="sm" onClick={() => confirmMatch(it.faktur_id, c, suggestions.data_type === "faktur_keluaran" ? "KELUARAN" : "MASUKAN")} data-testid={`match-confirm-${i}-${ci}`}><CheckCircle2 className="h-3 w-3 mr-1" />Konfirmasi</Button></TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent></Card>
                ))}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="history">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card><CardContent className="p-4">
              <div className="font-semibold mb-2">Riwayat Export</div>
              <Table><TableHeader><TableRow><TableHead>Jenis</TableHead><TableHead>Baris</TableHead><TableHead>Filter</TableHead><TableHead>Waktu</TableHead></TableRow></TableHeader>
                <TableBody>
                  {expHistory.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-4 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
                  {expHistory.map(h => (
                    <TableRow key={h.id} data-testid={`exp-hist-${h.id}`}>
                      <TableCell>{h.data_type}</TableCell>
                      <TableCell className="font-mono-num">{h.row_count}</TableCell>
                      <TableCell className="text-xs">{h.filters?.year || "-"}{h.filters?.month ? `-${String(h.filters.month).padStart(2,"0")}` : ""}</TableCell>
                      <TableCell className="text-xs">{formatDateID(h.exported_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="font-semibold mb-2">Riwayat Import</div>
              <Table><TableHeader><TableRow><TableHead>Jenis</TableHead><TableHead>Ringkasan</TableHead><TableHead>Status</TableHead><TableHead>Waktu</TableHead></TableRow></TableHeader>
                <TableBody>
                  {impHistory.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-4 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
                  {impHistory.map(h => (
                    <TableRow key={h.id} data-testid={`imp-hist-${h.id}`}>
                      <TableCell>{h.data_type}</TableCell>
                      <TableCell className="text-xs">V:{h.summary?.valid || 0} W:{h.summary?.warning || 0} E:{h.summary?.error || 0}</TableCell>
                      <TableCell><Badge className={h.status === "COMMITTED" ? "bg-emerald-600" : "bg-slate-500"}>{h.status}</Badge></TableCell>
                      <TableCell className="text-xs">{formatDateID(h.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}


/* ============================================================
 *  2) EXECUTIVE DASHBOARD PAJAK
 * ============================================================ */
export function ExecutiveDashboardPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState("");
  const [taxType, setTaxType] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (year) params.year = year;
      if (month) params.month = month;
      if (taxType) params.tax_type = taxType;
      const { data } = await api.get("/coretax5c/dashboard", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, taxType]);

  const ppn = data?.ppn; const pph = data?.pph || {}; const pay = data?.payments;
  const outstanding = data?.outstanding_liability; const spt = data?.spt || {};
  const comp = data?.compliance || {}; const integrity = data?.integrity;
  const coretax = data?.coretax;

  const posisiTone = ppn?.posisi_label === "KURANG_BAYAR" ? "amber" : ppn?.posisi_label === "LEBIH_BAYAR" ? "sky" : "slate";
  const overallCol = integrity?.overall === "SESUAI" ? "bg-emerald-600" : integrity?.overall === "BLOCKER" ? "bg-rose-600" : integrity?.overall === "PERLU_DITINJAU" ? "bg-amber-500" : "bg-slate-500";

  return (
    <div className="space-y-6 page-fade" data-testid="exec-dashboard-page">
      <div>
        <Crumb path={["Perpajakan", "Dasbor Eksekutif Pajak"]} />
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Dasbor Eksekutif Pajak</h1>
            <p className="text-slate-500 mt-1 text-sm">Ringkasan multi-jenis pajak berdasarkan filter periode. Data langsung dari sumber — tidak ada nilai simulasi.</p>
          </div>
          <div className="flex gap-2 items-end">
            <div><Label>Tahun</Label><Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || year)} className="w-24" data-testid="exec-year" /></div>
            <div><Label>Bulan</Label>
              <Select value={month === "" ? "ALL" : String(month)} onValueChange={(v) => setMonth(v === "ALL" ? "" : Number(v))}>
                <SelectTrigger className="w-28" data-testid="exec-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Semua</SelectItem>
                  {Array.from({length:12}).map((_,i) => <SelectItem key={i+1} value={String(i+1)}>{String(i+1).padStart(2,"0")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Jenis Pajak</Label>
              <Select value={taxType || "ALL"} onValueChange={(v) => setTaxType(v === "ALL" ? "" : v)}>
                <SelectTrigger className="w-32" data-testid="exec-tax-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Semua</SelectItem>
                  {TAX_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="exec-refresh"><RefreshCw className="h-4 w-4 mr-2" />Muat Ulang</Button>
          </div>
        </div>
      </div>

      {!data && loading && <div className="text-slate-500 text-sm py-4">Memuat dasbor…</div>}
      {!data && !loading && <div className="text-slate-500 text-sm py-4">TIDAK ADA DATA</div>}
      {data && (
        <>
          {/* Coretax status bar */}
          <div className={`border rounded-md p-3 flex items-center gap-3 ${coretax?.mode === "API" && !coretax?.api_configured ? "bg-amber-50 border-amber-300" : "bg-slate-50 border-slate-200"}`} data-testid="exec-coretax-bar">
            <BarChart3 className="h-5 w-5 text-slate-600" />
            <div className="flex-1 text-sm">
              <div className="font-semibold">Mode Coretax: {coretax?.mode}</div>
              <div className="text-xs text-slate-600">{coretax?.message}</div>
            </div>
            <Link to="/perpajakan/coretax-center"><Button size="sm" variant="outline" data-testid="exec-open-coretax">Buka Coretax Center</Button></Link>
          </div>

          {/* PPN */}
          <Card><CardContent className="p-4">
            <div className="flex items-center justify-between mb-2"><div className="font-semibold">PPN</div>
              {!ppn?.has_data && <Badge className="bg-slate-400">TIDAK ADA DATA</Badge>}
            </div>
            {ppn?.has_data ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="PPN Keluaran" value={formatRupiah(ppn.keluaran.amount)} sub={`${ppn.keluaran.count} faktur · DPP ${formatRupiah(ppn.keluaran.dpp)}`} tone="emerald" testid="exec-ppn-out" />
                <Stat label="PPN Masukan" value={formatRupiah(ppn.masukan.amount)} sub={`${ppn.masukan.count} faktur · DPP ${formatRupiah(ppn.masukan.dpp)}`} tone="sky" testid="exec-ppn-in" />
                <Stat label={`Posisi PPN (${ppn.posisi_label})`} value={formatRupiah(Math.abs(ppn.posisi_ppn))} tone={posisiTone} testid="exec-ppn-posisi" />
                <div className="flex items-center justify-center border rounded p-3 bg-slate-50 text-slate-500 text-xs">
                  <Link to="/perpajakan/ppn" className="hover:underline">Buka PPN Hub →</Link>
                </div>
              </div>
            ) : (
              <div className="text-slate-400 text-sm py-3">Tidak ada faktur PPN pada masa ini.</div>
            )}
          </CardContent></Card>

          {/* PPh per type */}
          <Card><CardContent className="p-4">
            <div className="font-semibold mb-2">PPh per Jenis</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {["PPh21", "PPh22", "PPh23", "PPh25", "PPhFinal"].map(tt => {
                const d = pph[tt];
                if (!d) return (
                  <div key={tt} className="border rounded p-3 bg-slate-50 opacity-60">
                    <div className="text-xs text-slate-500">{tt}</div>
                    <div className="text-xs text-slate-400 mt-1">Difilter keluar</div>
                  </div>
                );
                return (
                  <div key={tt} className={`border rounded p-3 ${d.count > 0 ? "bg-white border-slate-200" : "bg-slate-50 border-slate-200"}`} data-testid={`exec-${tt.toLowerCase()}`}>
                    <div className="text-xs text-slate-500">{tt}</div>
                    <div className="font-mono-num text-lg font-semibold">{d.count > 0 ? formatRupiah(d.amount) : "—"}</div>
                    <div className="text-xs text-slate-400 mt-1">{d.count > 0 ? `${d.count} transaksi` : "TIDAK ADA DATA"}</div>
                  </div>
                );
              })}
            </div>
          </CardContent></Card>

          {/* Payments + Outstanding */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card><CardContent className="p-4">
              <div className="font-semibold mb-2">Pembayaran Pajak</div>
              {pay?.has_data ? (
                <div className="grid grid-cols-3 gap-3">
                  <Stat label="Total Dibayar" value={formatRupiah(pay.amount)} tone="emerald" testid="exec-pay-total" />
                  <Stat label="Jumlah Pembayaran" value={pay.count} testid="exec-pay-count" />
                  <Stat label="Dengan NTPN" value={pay.with_ntpn} tone={pay.with_ntpn === pay.count ? "emerald" : "amber"} testid="exec-pay-ntpn" />
                </div>
              ) : <div className="text-slate-400 text-sm py-3">TIDAK ADA DATA pembayaran pada masa ini.</div>}
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="font-semibold mb-2">Kewajiban Pajak Outstanding</div>
              {outstanding?.count > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  <Stat label="Total Outstanding" value={formatRupiah(outstanding.amount)} tone="amber" testid="exec-out-amount" />
                  <Stat label="Jumlah Kewajiban" value={outstanding.count} testid="exec-out-count" />
                </div>
              ) : <div className="text-slate-400 text-sm py-3">TIDAK ADA kewajiban outstanding.</div>}
            </CardContent></Card>
          </div>

          {/* SPT + Compliance + Docs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card><CardContent className="p-4">
              <div className="font-semibold mb-2">Status SPT</div>
              <div className="space-y-1 text-sm">
                {Object.entries(spt).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-slate-100 pb-1">
                    <span className="text-slate-600">{k}</span>
                    <span className={`font-mono ${v > 0 ? "font-semibold" : "text-slate-400"}`}>{v}</span>
                  </div>
                ))}
              </div>
              <Link to="/perpajakan/spt" className="text-xs text-sky-600 hover:underline mt-2 inline-block" data-testid="exec-open-spt">Buka Kertas Kerja SPT →</Link>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="font-semibold mb-2">Ringkasan Kepatuhan</div>
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Blocker" value={comp.blocker || 0} tone={comp.blocker > 0 ? "rose" : "slate"} testid="exec-comp-blocker" />
                <Stat label="Warning" value={comp.warning || 0} tone={comp.warning > 0 ? "amber" : "slate"} testid="exec-comp-warning" />
                <Stat label="Info" value={comp.info || 0} tone="slate" testid="exec-comp-info" />
              </div>
              <Link to="/perpajakan/coretax-center" className="text-xs text-sky-600 hover:underline mt-2 inline-block" data-testid="exec-open-comp">Buka Compliance Wall →</Link>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="font-semibold mb-2">Integritas Pajak</div>
              <div className="flex items-center gap-2 mb-3">
                <Badge className={overallCol} data-testid="exec-integrity-badge">{integrity?.overall || "-"}</Badge>
                <span className="text-xs text-slate-500">10-way rekonsiliasi</span>
              </div>
              <div className="text-sm space-y-1">
                <div className="flex justify-between"><span className="text-emerald-700">SESUAI</span><span className="font-mono">{integrity?.sesuai || 0}</span></div>
                <div className="flex justify-between"><span className="text-amber-700">PERLU DITINJAU</span><span className="font-mono">{integrity?.perlu_ditinjau || 0}</span></div>
                <div className="flex justify-between"><span className="text-rose-700">BLOCKER</span><span className="font-mono">{integrity?.blocker || 0}</span></div>
              </div>
              <Link to="/perpajakan/integritas-final" className="text-xs text-sky-600 hover:underline mt-2 inline-block" data-testid="exec-open-integrity">Buka Pusat Integritas →</Link>
            </CardContent></Card>
          </div>

          {/* Additional counts */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Faktur Aktif" value={data.faktur_count_active || 0} tone="slate" testid="exec-faktur-count" />
            <Stat label="Bukti Potong Aktif" value={data.bukti_potong_count_active || 0} tone="slate" testid="exec-bp-count" />
            <Stat label="Total Dokumen Arsip" value={data?.documents?.total || 0} tone="slate" testid="exec-docs-count" />
            <div className="border rounded p-3 bg-slate-50 flex items-center justify-center">
              <div className="text-xs text-slate-500 text-center">Digenerate<br /><b>{formatDateID(data.generated_at)}</b></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


/* ============================================================
 *  3) FAKTUR PENGGANTI DRILL-DOWN (embedded in Faktur detail)
 * ============================================================ */
export function FakturPenggantiDrillCard({ fakturId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!fakturId) return;
    setLoading(true);
    api.get(`/tax/faktur/${fakturId}/pengganti-drilldown`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [fakturId]);

  if (!fakturId) return null;
  return (
    <Card className="border-amber-300 border-2"><CardContent className="p-4 space-y-3" data-testid="faktur-pengganti-drill">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><GitBranch className="h-4 w-4 text-amber-600" /><b>Drill-down Rantai Faktur Pengganti</b></div>
        <Button size="sm" variant="ghost" onClick={onClose}>Tutup</Button>
      </div>
      {loading && <div className="text-slate-500 text-sm">Memuat…</div>}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Jumlah Pengganti" value={data.delta_summary?.count_pengganti || 0} tone="amber" testid="drill-count-pengganti" />
            <Stat label="Total Delta PPN" value={formatRupiah(data.delta_summary?.total_delta_ppn || 0)} tone={data.delta_summary?.total_delta_ppn > 0 ? "amber" : data.delta_summary?.total_delta_ppn < 0 ? "sky" : "slate"} testid="drill-total-delta" />
          </div>
          <div>
            <div className="font-semibold text-sm mb-1">Rantai Faktur (kronologis)</div>
            <Table>
              <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Nomor</TableHead><TableHead>Tanggal</TableHead><TableHead>Status</TableHead><TableHead>DPP</TableHead><TableHead>PPN</TableHead><TableHead>Δ PPN</TableHead><TableHead>Alasan</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.chain.map((f, i) => (
                  <TableRow key={f.id} data-testid={`drill-chain-${i}`} className={f.id === data.focus.id ? "bg-amber-50" : ""}>
                    <TableCell>{i === 0 ? <Badge className="bg-slate-600">ORIGINAL</Badge> : <Badge className="bg-amber-600">P{i}</Badge>}</TableCell>
                    <TableCell className="font-mono text-xs">{f.faktur_number}</TableCell>
                    <TableCell className="text-xs">{formatDateID(f.faktur_date)}</TableCell>
                    <TableCell><Badge className="bg-slate-500 text-xs">{f.status}</Badge></TableCell>
                    <TableCell className="font-mono-num text-xs">{formatRupiah(f.dpp)}</TableCell>
                    <TableCell className="font-mono-num text-xs">{formatRupiah(f.ppn)}</TableCell>
                    <TableCell className="font-mono-num text-xs">{i === 0 ? "-" : formatRupiah(f.pengganti_delta_ppn || 0)}</TableCell>
                    <TableCell className="text-xs">{f.pengganti_reason || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {data.adjust_journals?.length > 0 && (
            <div>
              <div className="font-semibold text-sm mb-1">Jurnal Auto-GL <span className="font-normal text-xs text-slate-500">(AUTO_FAKTUR_PENGGANTI)</span></div>
              <Table>
                <TableHeader><TableRow><TableHead>Nomor Jurnal</TableHead><TableHead>Tanggal</TableHead><TableHead>Deskripsi</TableHead><TableHead>Debit</TableHead><TableHead>Kredit</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.adjust_journals.map(j => (
                    <TableRow key={j.id} data-testid={`drill-journal-${j.id}`}>
                      <TableCell className="font-mono text-xs">{j.journal_number || j.number}</TableCell>
                      <TableCell className="text-xs">{formatDateID(j.date)}</TableCell>
                      <TableCell className="text-xs">{j.description || j.memo}</TableCell>
                      <TableCell className="font-mono-num text-xs">{formatRupiah((j.lines || []).reduce((s, l) => s + (l.debit || 0), 0))}</TableCell>
                      <TableCell className="font-mono-num text-xs">{formatRupiah((j.lines || []).reduce((s, l) => s + (l.credit || 0), 0))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Link to="/akuntansi/jurnal" className="text-xs text-sky-600 hover:underline mt-1 inline-block" data-testid="drill-open-journal">Buka Modul Jurnal →</Link>
            </div>
          )}
          {(!data.original && data.chain.length === 1) && (
            <div className="text-xs text-slate-400 italic">Faktur ini belum memiliki pengganti.</div>
          )}
        </>
      )}
    </CardContent></Card>
  );
}
